import { I18nProvider } from '@planetra/i18n';
import { TooltipProvider } from '@planetra/ui';
import { Suspense, type ReactNode } from 'react';
import { RouterProvider } from 'react-router';
import { ApiProvider } from '../api';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ErrorFallback } from '../components/ErrorFallback';
import { RouteFallback } from '../components/RouteFallback';
import { createRouter } from './router';

const router = createRouter();

/**
 * Provider stack.
 *
 * Order is load-bearing. The error boundary is outermost so it can catch a
 * provider that throws on mount; i18n sits above it inside so the fallback can
 * be translated — a boundary that renders untranslated English at the moment
 * something breaks is the one place a user is least able to cope with it.
 *
 * `ApiProvider` sits inside `I18nProvider` because it reads the active locale
 * to keep `Accept-Language` current (P0-WEB-04).
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <I18nProvider>
      <ApiProvider>
        <TooltipProvider>{children}</TooltipProvider>
      </ApiProvider>
    </I18nProvider>
  );
}

export function App() {
  return (
    <Providers>
      <ErrorBoundary
        context={{ boundary: 'root' }}
        fallback={({ reset }) => (
          <ErrorFallback
            onRetry={reset}
            onReload={() => {
              window.location.reload();
            }}
          />
        )}
      >
        <Suspense fallback={<RouteFallback />}>
          <RouterProvider router={router} />
        </Suspense>
      </ErrorBoundary>
    </Providers>
  );
}
